import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit, Trash2, Shield } from 'lucide-react';
import { permissionService } from '../../services/permissionService';
import { GroupedPermissions } from '../../types';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import PermissionFormModal from './PermissionFormModal';

const PermissionsPage: React.FC = () => {
  const [groupedPermissions, setGroupedPermissions] = useState<GroupedPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<any>(null);
  const [deletingPermission, setDeletingPermission] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await permissionService.getGrouped();

      // Filtrar por busca se houver
      if (search) {
        const filtered = data
          .map((group) => ({
            ...group,
            permissions: group.permissions.filter(
              (p) =>
                p.module.toLowerCase().includes(search.toLowerCase()) ||
                p.action.toLowerCase().includes(search.toLowerCase()) ||
                p.description?.toLowerCase().includes(search.toLowerCase())
            ),
          }))
          .filter((group) => group.permissions.length > 0);
        setGroupedPermissions(filtered);
      } else {
        setGroupedPermissions(data);
      }
    } catch (err: any) {
      setError('Erro ao carregar permissões');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const handleAdd = () => {
    setEditingPermission(null);
    setIsFormOpen(true);
  };

  const handleEdit = (permission: any) => {
    setEditingPermission(permission);
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingPermission) return;

    try {
      await permissionService.delete(deletingPermission.id);
      setSuccess('Permissão excluída com sucesso!');
      setDeletingPermission(null);
      loadPermissions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir permissão');
      setDeletingPermission(null);
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingPermission(null);
    loadPermissions();
    setSuccess(
      editingPermission ? 'Permissão atualizada com sucesso!' : 'Permissão criada com sucesso!'
    );
  };

  if (loading && groupedPermissions.length === 0) {
    return (
      <div className="p-8 flex justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Alerts */}
      {error && (
        <Alert type="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Permissões do Sistema</h2>
          <p className="text-gray-600 mt-1">
            Gerencie todas as permissões disponíveis no sistema
          </p>
        </div>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova Permissão
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder="Buscar permissão..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="h-5 w-5" />}
        />
      </div>

      {/* Grouped Permissions */}
      <div className="space-y-6">
        {groupedPermissions.map((group) => (
          <div key={group.module} className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 bg-gray-50 border-b flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900 uppercase">{group.module}</h3>
              <span className="text-sm text-gray-500">({group.permissions.length} permissões)</span>
            </div>
            <div className="divide-y divide-gray-200">
              {group.permissions.map((permission) => (
                <div
                  key={permission.id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-blue-600">
                        {permission.action}
                      </span>
                    </div>
                    {permission.description && (
                      <p className="text-sm text-gray-600 mt-1">{permission.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(permission)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeletingPermission(permission)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {groupedPermissions.length === 0 && (
          <div className="text-center py-12 text-gray-500">Nenhuma permissão encontrada</div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <PermissionFormModal
          permission={editingPermission}
          onClose={() => {
            setIsFormOpen(false);
            setEditingPermission(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingPermission && (
        <Modal isOpen={true} onClose={() => setDeletingPermission(null)} title="Confirmar Exclusão">
          <div className="p-6">
            <p className="text-gray-700 mb-6">
              Tem certeza que deseja excluir a permissão{' '}
              <strong>
                {deletingPermission.module}.{deletingPermission.action}
              </strong>
              ? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeletingPermission(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Excluir
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PermissionsPage;
